import {
  I2cFileUpload,
  I2cInput,
  I2cButton,
  I2cIcon,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import FilesUploadService from "../../../Services/FilesUploadService";
import { useMutation } from "react-query";
import { OverlayLoader } from "../../../Components/Loader/Loader";

// Map file extensions to MIME types
const getMimeType = (fileName) => {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  const mimeTypes = {
    pdf: "application/pdf",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xls: "application/vnd.ms-excel",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    doc: "application/msword",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    svg: "image/svg+xml",
    zip: "application/zip",
    rar: "application/x-rar-compressed",
    "7z": "application/x-7z-compressed",
    tar: "application/x-tar",
    gz: "application/gzip",
  };
  return mimeTypes[ext] ?? "application/octet-stream";
};

const validationSchema = Yup.object().shape({
  useCaseDocumentTemplateID: Yup.string().required("Template ID is required"),
  useCaseDocumentTemplateFiles: Yup.array().optional().of(Yup.string()),
});

const UseCaseDocument = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  const fileUploadRef = useRef(null);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      useCaseDocumentTemplateID: "",
      useCaseDocumentTemplateFiles: [],
    },
  });

  const {
    control,
    formState: { errors },
  } = methods;

  // Setup dropzone configuration
  useEffect(() => {
    customElements.whenDefined("i2c-file-upload").then(() => {
      if (fileUploadRef.current !== null) {
        const myDropzone = fileUploadRef.current.getDropZone();
        const token = localStorage.getItem("token");
        myDropzone.options.url =
          process.env.REACT_APP_API_BASE_URL + "api/file/upload/pdf";
        myDropzone.options.headers = {
          "X-Requested-With": null,
          "Access-Control-Allow-Headers": null,
          Authorization: "Bearer " + token,
        };
      }
    });
  }, []);

  // Load existing data from formDataJson
  useEffect(() => {
    if (formDataJson && formDataJson[10] && formDataJson[10]["10_0"]) {
      const data = formDataJson[10]["10_0"];
      if (data.useCaseDocumentTemplateID) {
        methods.setValue(
          "useCaseDocumentTemplateID",
          data.useCaseDocumentTemplateID,
          { shouldValidate: true },
        );
      }
      if (
        data.useCaseDocumentTemplateFiles &&
        Array.isArray(data.useCaseDocumentTemplateFiles)
      ) {
        methods.setValue(
          "useCaseDocumentTemplateFiles",
          data.useCaseDocumentTemplateFiles,
          { shouldValidate: true },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDataJson]);

  // Handle value changes
  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      10: {
        "10_0": { ...values },
      },
    });
  };
  // Trigger validation on mount only
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form valid state - use useCallback to stabilize setFormValidStates
  useEffect(() => {
    const isValid = methods.formState.isValid;
    if (setFormValidStates) {
      setFormValidStates("10_0", isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.formState.isValid]);

  // File upload mutation
  const fileUpload = useMutation({
    mutationFn: async (formData) => {
      return await FilesUploadService.uploadFile(formData);
    },
    onSuccess: (data) => {
      const currentFiles =
        methods.getValues("useCaseDocumentTemplateFiles") || [];
      methods.setValue("useCaseDocumentTemplateFiles", [
        ...currentFiles,
        data?.data?.url,
      ]);
      methods.trigger();
      handleValueChanges();
    },
    onError: (error) => {
      console.log("API error:", error);

      let errorMessage = "An error occurred during file upload.";

      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors?.files;
        if (errors && Array.isArray(errors)) {
          errorMessage = errors.join(" ");
        }
      } else if (error.response?.status === 404) {
        errorMessage = error.response?.data?.error || errorMessage;
      }

      console.error("Upload error:", errorMessage);
      methods.setError("useCaseDocumentTemplateFiles", {
        type: "manual",
        message: errorMessage,
      });
      methods.trigger();
    },
  });

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      await fileUpload.mutate(formData);
    } catch (error) {
      console.log(error);
    }
  };

  // File download mutation
  const fileDownload = useMutation({
    mutationFn: async (fileName) => {
      return await FilesUploadService.downloadFile(fileName);
    },
    onSuccess: (data, fileName) => {
      const contentType =
        data.headers?.["content-type"] || getMimeType(fileName);
      const url = window.URL.createObjectURL(
        new Blob([data.data], { type: contentType }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName.split("/").pop());
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (e) => {
      console.log("download error", e);
    },
  });

  // Handle file download
  const downloadFile = async (fileName) => {
    try {
      await fileDownload.mutate(fileName);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <React.Fragment>
      <div className="forms-instance" i2c-grid="grid gap-sm">
        <div i2c-grid="12">
          <div i2c-grid="grid 4@md">
            <div className="single-field-wrapper mt-16">
              <Controller
                name="useCaseDocumentTemplateID"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Template ID"
                    placeholder="Enter Template ID"
                    required
                    size="large"
                    name="useCaseDocumentTemplateID"
                    value={methods.watch("useCaseDocumentTemplateID")}
                    onI2cInput={(e) => {
                      field.onChange(e);
                      methods.trigger();
                      handleValueChanges();
                    }}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.useCaseDocumentTemplateID &&
                  methods.formState.touchedFields.useCaseDocumentTemplateID
                    ? true
                    : false
                }
                message={
                  errors.useCaseDocumentTemplateID?.message ?? "Invalid input"
                }
              />
            </div>
          </div>
          <div>
            <div className="mb-16 mt-16 custom-label-light">
              Please provide the required document(s) in attachment.
            </div>
            <I2cFileUpload
              ref={fileUploadRef}
              autoProcessQueue={false}
              no-mobile-controls
              multiple
              max-files={10}
              max-file-size={5242880}
              className="customFileUpload"
              setAcceptedFiles=".pdf,.xlsx,.doc,.png,.jpg,.jpeg, .svg, .zip, .rar, .7z, .tar, .gz"
              icon="linear-document-upload"
              label="Click to upload"
              caption="or drag and drop"
              name="useCaseDocumentTemplateFiles"
              message="Most file types are accepted (up to 5MB each). For security reasons .exe, .bat, .cmd, .js, .jar, .msi, .app, .deb, and .rpm are not allowed."
              onI2cFileRemoved={(e) => {
                const currentFiles =
                  methods.getValues("useCaseDocumentTemplateFiles") || [];
                const removedFileName = e.detail?.file?.upload?.filename;

                if (removedFileName) {
                  const updatedFiles = currentFiles.filter(
                    (file) => !file.includes(removedFileName),
                  );
                  methods.setValue(
                    "useCaseDocumentTemplateFiles",
                    updatedFiles,
                  );
                } else if (
                  formDataJson &&
                  formDataJson[10] &&
                  formDataJson[10]["10_0"]?.useCaseDocumentTemplateFiles?.length
                ) {
                  methods.setValue(
                    "useCaseDocumentTemplateFiles",
                    formDataJson[10]["10_0"]?.useCaseDocumentTemplateFiles,
                  );
                } else {
                  methods.setValue("useCaseDocumentTemplateFiles", []);
                }
                methods.trigger();
                handleValueChanges();
              }}
              onI2cAddedFile={(e) => {
                console.log("File added", e);
                const dropzone = fileUploadRef.current.getDropZone();
                const addedFile = dropzone.files[dropzone.files.length - 1];
                handleFileUpload(addedFile);
              }}
            />
            <ErrorMessage
              show={
                errors.useCaseDocumentTemplateFiles &&
                methods.formState.touchedFields.useCaseDocumentTemplateFiles
                  ? true
                  : false
              }
              message={
                errors.useCaseDocumentTemplateFiles?.message ?? "Invalid input"
              }
            />
          </div>

          {/* Display download buttons for each uploaded file from backend */}
          {Object.keys(formDataJson ?? {}).length > 0 &&
            formDataJson[10]?.["10_0"]?.useCaseDocumentTemplateFiles &&
            Array.isArray(
              formDataJson[10]["10_0"].useCaseDocumentTemplateFiles,
            ) &&
            formDataJson[10]["10_0"].useCaseDocumentTemplateFiles.length >
              0 && (
              <div className="mt-16">
                <div className="custom-label-light mb-12">Uploaded Files:</div>
                {formDataJson[10]["10_0"].useCaseDocumentTemplateFiles.map(
                  (file, index) => (
                    <div key={index} className="mb-8">
                      <I2cButton
                        size="small"
                        variant="link color"
                        onClick={() => {
                          downloadFile(file);
                        }}
                      >
                        <I2cIcon
                          slot="prefix"
                          name="linear-document-download"
                          label="download"
                        ></I2cIcon>{" "}
                        Download File {index + 1}
                      </I2cButton>
                    </div>
                  ),
                )}
              </div>
            )}

          {/* Display download buttons for currently uploaded files in this session */}
          {methods.watch("useCaseDocumentTemplateFiles") &&
            Array.isArray(methods.watch("useCaseDocumentTemplateFiles")) &&
            methods.watch("useCaseDocumentTemplateFiles").length > 0 &&
            (!formDataJson[10]?.["10_0"]?.useCaseDocumentTemplateFiles ||
              methods.watch("useCaseDocumentTemplateFiles").length >
                formDataJson[10]["10_0"].useCaseDocumentTemplateFiles
                  .length) && (
              <div className="mt-16">
                <div className="custom-label-light mb-12">
                  Current Session Files:
                </div>
                {methods
                  .watch("useCaseDocumentTemplateFiles")
                  .map((file, index) => (
                    <div key={index} className="mb-8">
                      <I2cButton
                        size="small"
                        variant="link color"
                        onClick={() => {
                          downloadFile(file);
                        }}
                      >
                        <I2cIcon
                          slot="prefix"
                          name="linear-document-download"
                          label="download"
                        ></I2cIcon>{" "}
                        Download File {index + 1}
                      </I2cButton>
                    </div>
                  ))}
              </div>
            )}
        </div>
      </div>
      {fileDownload.isLoading && <OverlayLoader />}
    </React.Fragment>
  );
};

export default UseCaseDocument;
